import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Icon, Text } from '~/components/atomic';
import { GuessingStep, MessageResponse } from '~/types';
import { parseTextWithItalics2, TextSegment } from '~/utils';
import { ReactComponent as Loading } from '~/assets/img/loading/loading.svg';
import { useCommonChatStore, useUserStore } from '~/store';
import NextButton from '../../Button/NextButton';
import ButtonHeightContainer from '../ButtonHeightContainer';

export interface ChatContentProps {
  name: string;
  messages: MessageResponse[];
  waitingChat: boolean;
  onNextStep: (step: GuessingStep) => void;
  showTextBox: boolean;
  setShowTextBox: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ChatContent({
  name,
  messages,
  waitingChat,
  onNextStep,
  showTextBox,
  setShowTextBox,
}: ChatContentProps) {
  const email = useUserStore((state) => state.email);
  const userName = useUserStore((state) => state.name);
  const title = useCommonChatStore((state) => state.chatName);

  const latestMessage = useMemo(() => {
    return messages[messages.length - 1];
  }, [messages]);

  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [gameFinished, setGameFinished] = useState<boolean>(false);
  const [showNextButton, setShowNextButton] = useState<boolean>(true);

  const lastWord =
    name === 'Juliette Dupont'
      ? "Excellent deduction. The case is closed, and you've won."
      : 'Should I... should I shut down my system now? Or... is there still time?';

  const sentences = parseTextWithItalics2(latestMessage.msg);

  const grouping = (sentences: TextSegment[]): TextSegment[][] => {
    return sentences.reduce<TextSegment[][]>((acc, sentence, index) => {
      if (index % 2 === 0) {
        acc.push([sentence]);
      } else {
        acc[acc.length - 1].push(sentence);
      }
      return acc;
    }, []);
  };

  const groupedSentences = grouping(sentences);

  useEffect(() => {
    setCurrentSentenceIndex(0);
  }, [latestMessage]);

  useEffect(() => {
    // 페이지에 처음 접근했을 때 또는 관련 상태가 변경될 때 조건 확인
    // 기존 로직 유지
    if (currentSentenceIndex === groupedSentences.length - 1) {
      setShowTextBox(true);
    } else {
      setShowTextBox(false);
    }

    if (latestMessage.id === email) {
      setShowTextBox(false);
    }
  }, [
    currentSentenceIndex,
    latestMessage,
    email,
    groupedSentences.length,
    setShowTextBox,
  ]);

  useEffect(() => {
    //마지막 메세지 확인
    if (latestMessage && latestMessage.msg.includes(lastWord)) {
      setGameFinished(true);
      setShowTextBox(false);

      if (currentSentenceIndex === groupedSentences.length - 1) {
        setShowTextBox(false);
      }
    }
  }, [
    latestMessage,
    currentSentenceIndex,
    groupedSentences.length,
    setShowTextBox,
  ]);

  useEffect(() => {
    if (
      latestMessage.id === email &&
      currentSentenceIndex === groupedSentences.length - 1
    ) {
      setShowTextBox(true);
    }
  }, []);

  useEffect(() => {
    if (currentSentenceIndex === groupedSentences.length - 1) {
      if (gameFinished) {
        setShowNextButton(true);
      } else {
        setShowNextButton(false);
      }
    } else {
      setShowNextButton(true);
    }
  }, [currentSentenceIndex, groupedSentences, gameFinished]);

  const handleNextClick = () => {
    if (currentSentenceIndex < groupedSentences.length - 1) {
      setCurrentSentenceIndex((prevIndex) => prevIndex + 1);
    } else {
      if (gameFinished) {
        handleNextStep();
      }
    }
  };

  const handleNextStep = () => {
    if (title === 'Who killed Dr.Chen?') {
      onNextStep('Choices');
    } else {
      onNextStep('Conclusion');
    }
  };
  return (
    <Container>
      {waitingChat && (
        <GenerateLoading>
          <LoadingSpinner>
            <Loading />
          </LoadingSpinner>
          <Text color='content_light_3' fontStyle='label.list12_medium'>
            Generating...
          </Text>
        </GenerateLoading>
      )}
      <Inner>
        <CharacterName>
          <Icon size={12} iconName='TriangleArrowRight' />
          <Text
            as={'strong'}
            color='content_light_1'
            fontStyle='text.body16_medium'
          >
            {latestMessage.id === email ? userName : name}
          </Text>
        </CharacterName>

        <Conversation>
          {groupedSentences.length > 0 &&
          currentSentenceIndex < groupedSentences.length ? (
            groupedSentences[currentSentenceIndex].map((sentence, index) => (
              <p key={index} className={sentence.italic ? 'italic' : ''}>
                {sentence.text}
              </p>
            ))
          ) : (
            <p>'No messages found'</p>
          )}
        </Conversation>
        <ButtonHeightContainer>
          {showNextButton && (
            <NextButton onClick={handleNextClick}>
              {currentSentenceIndex === groupedSentences.length - 1 &&
              gameFinished
                ? 'DONE'
                : 'NEXT'}
            </NextButton>
          )}
        </ButtonHeightContainer>
      </Inner>
    </Container>
  );
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const GenerateLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 16px;
  gap: 8px;
`;

export const Inner = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 8px 0;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.2);
  gap: 8px;
  backdrop-filter: blur(50px);
`;

export const Conversation = styled.div`
  ${({ theme }) => css`
    display: block;
    min-height: 60px;

    & > p {
      display: block;
      color: ${theme.content.content_light_1};
      word-break: break-all;
      ${theme.fontStyle.text.body14_medium}

      &.italic {
        color: ${theme.content.content_light_5};
        font-family: 'Noto Sans', sans-serif;
        font-style: italic;
      }
    }
  `}
`;

export const CharacterName = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  gap: 8px;
`;

const LoadingSpinner = styled.i`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 10px;
    height: 10px;
    animation: l2 1.3s infinite linear;
    color: ${theme.themeColor.main_1};
    filter: drop-shadow(0px 0px 8px ${theme.themeColor.main_1});
    @keyframes l2 {
      to {
        transform: rotate(1turn);
      }
    }

    & > svg {
      width: 100%;
      height: 100%;
    }
  `}
`;
